import type { Location, Playlist, Screen, ScreenGroup } from '@ghostscreen/contracts';
import { i18n } from '../../i18n/i18n';
import {
  getCardConnectionStatus,
  type CardConnectionStatus,
} from '../../lib/dashboard/screenStatus';
import { locationNameForScreen } from '../../lib/schedule/timezoneForScreen';

export type GroupMemberChip = {
  id: string;
  name: string;
  status: CardConnectionStatus;
  dot: string;
};

export type GroupCardModel = {
  group: ScreenGroup;
  meta: string;
  health: string;
  healthColor: string;
  members: GroupMemberChip[];
  noMembers: boolean;
  /** Shared playlist id, or '' when none / mixed. */
  playlistValue: string;
  playlistPlaceholder: string;
  memberScreens: Screen[];
};

const STATUS_DOT: Record<CardConnectionStatus, string> = {
  online: 'var(--gs-ok)',
  offline: 'var(--gs-bad)',
  unknown: 'var(--gs-warn)',
  unpaired: 'var(--gs-muted)',
};

export function statusLabel(status: CardConnectionStatus): string {
  return i18n.t(`groups:status.${status}`);
}

export function buildGroupCardModel(input: {
  group: ScreenGroup;
  memberIds: string[];
  screens: Screen[];
  assignmentByScreenId: ReadonlyMap<string, string | null>;
}): GroupCardModel {
  const { group, memberIds, screens, assignmentByScreenId } = input;
  const memberScreens = memberIds
    .map((id) => screens.find((screen) => screen.id === id))
    .filter((screen): screen is Screen => Boolean(screen));

  const offline = memberScreens.filter((screen) => {
    const status = getCardConnectionStatus(screen);
    return status === 'offline' || status === 'unknown';
  }).length;
  const online = memberScreens.filter(
    (screen) => getCardConnectionStatus(screen) === 'online',
  ).length;

  const paired = memberScreens.filter(
    (screen) => getCardConnectionStatus(screen) !== 'unpaired',
  );
  const playlistIds = [
    ...new Set(
      paired
        .map((screen) => assignmentByScreenId.get(screen.id) ?? null)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const mixed = playlistIds.length > 1;
  const playlistValue = playlistIds.length === 1 ? playlistIds[0]! : '';

  let health = i18n.t('groups:card.healthNotSetUp');
  let healthColor = 'var(--gs-muted)';
  if (!memberScreens.length) {
    health = i18n.t('groups:card.healthEmpty');
    healthColor = 'var(--gs-muted)';
  } else if (offline) {
    health = i18n.t('groups:card.healthNotResponding', { count: offline });
    healthColor = 'var(--gs-bad)';
  } else if (online) {
    health = i18n.t('groups:card.healthOnline', { count: online });
    healthColor = 'var(--gs-ok)';
  }

  return {
    group,
    meta: i18n.t('groups:card.screens', { count: memberScreens.length }),
    health,
    healthColor,
    members: memberScreens.map((screen) => {
      const status = getCardConnectionStatus(screen);
      return {
        id: screen.id,
        name: screen.name,
        status,
        dot: STATUS_DOT[status],
      };
    }),
    noMembers: memberScreens.length === 0,
    playlistValue,
    playlistPlaceholder: mixed
      ? i18n.t('groups:card.playlistMixed')
      : i18n.t('groups:card.playlistNone'),
    memberScreens,
  };
}

export type GroupScreenPreset = {
  label: string;
  name: string;
  screenIds: string[];
};

/** Quick-start presets: every screen, plus one per location. */
export function buildGroupPresets(input: {
  screens: Screen[];
  locations: Location[];
}): GroupScreenPreset[] {
  const { screens, locations } = input;
  const presets: GroupScreenPreset[] = [
    {
      label: i18n.t('groups:presets.everyScreen'),
      name: i18n.t('groups:presets.everyScreen'),
      screenIds: screens.map((screen) => screen.id),
    },
  ];

  for (const location of locations) {
    const ids = screens
      .filter((screen) => screen.locationId === location.id)
      .map((screen) => screen.id);
    if (!ids.length) continue;
    presets.push({
      label: location.name,
      name: i18n.t('groups:presets.locationScreens', { location: location.name }),
      screenIds: ids,
    });
  }

  // Screens with no location still get a bucket when needed.
  const unlocated = screens.filter((screen) => !screen.locationId);
  if (unlocated.length) {
    presets.push({
      label: i18n.t('groups:presets.noLocation'),
      name: i18n.t('groups:presets.noLocationScreens'),
      screenIds: unlocated.map((screen) => screen.id),
    });
  }

  return presets.filter((preset) => preset.screenIds.length > 0);
}

export type GroupDrawerLocationSection = {
  locationId: string | null;
  name: string;
  screens: Screen[];
};

export function groupScreensByLocation(
  screens: Screen[],
  locations: Location[],
): GroupDrawerLocationSection[] {
  const byId = new Map(locations.map((location) => [location.id, location.name]));
  const buckets = new Map<string, Screen[]>();

  for (const screen of screens) {
    const key = screen.locationId ?? '__none__';
    const list = buckets.get(key) ?? [];
    list.push(screen);
    buckets.set(key, list);
  }

  const sections: GroupDrawerLocationSection[] = [];
  for (const location of locations) {
    const list = buckets.get(location.id);
    if (!list?.length) continue;
    sections.push({ locationId: location.id, name: location.name, screens: list });
    buckets.delete(location.id);
  }
  const none = buckets.get('__none__');
  if (none?.length) {
    sections.push({ locationId: null, name: i18n.t('groups:presets.noLocation'), screens: none });
  }
  return sections;
}

export function locationLabelForScreen(screen: Screen, locations: Location[]): string {
  return locationNameForScreen(screen, locations) ?? i18n.t('groups:presets.noLocation');
}

export function playlistOptions(
  playlists: Playlist[],
  placeholder: string,
): Array<{ id: string; name: string }> {
  return [{ id: '', name: placeholder }, ...playlists.map((p) => ({ id: p.id, name: p.name }))];
}
